/* eslint-disable eqeqeq */
import _ from "lodash";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/api";
import useUrlQuery from "../hooks/useUrlQuery";
import { Actions } from "../slices/actions";
import { leadDataSelector, proposalSelector } from "../slices/selectors";
import { carePayment } from "../utils/utilities";
import { useGetUserDetails } from "./globalService";

export const useStoreProposalDetails = ({ index }) => {
  const { userDetails } = useGetUserDetails();
  const { getQuery } = useUrlQuery();
  const { proposalData } = useSelector(proposalSelector);
  const proposerFormData = proposalData?.[`proposar`];
  const enquiry_id = getQuery("enquiry_id");
  const { isLoading, mutate } = useMutation(
    (req) =>
      api.post("/store/proposal/detail", {
        ...req,
        enquiry_id,
        user_id: userDetails?.user_id,
        selected_plan_id: userDetails?.selected_plan?.id,
        insurance_company_id: userDetails?.selected_plan?.ic_id,
        ic_id: userDetails?.selected_plan?.ic_id,
      }),
    {
      onSuccess: (data, variables) => {
        if (data.data.data?.primary_proposer_id)
          Actions.setPrimaryProposerId(data?.data?.data?.primary_proposer_id);
        if (data?.data?.status && variables?.ckyc_data?.ovdkyc)
          Actions.setCKycStatus(true);
        if (data?.data?.status) Actions.setActive(index + 1);
        if (
          !_.isEqual(proposerFormData, variables?.additional_data?.proposar) &&
          Object.keys(variables?.additional_data)?.length === 1
        ) {
          Actions.setActiveStep(0);
          Actions.setCKycFailCount(0);
          Actions.setCKycStatus(false);
          Actions.resetCkycUploadStatus();
        }
      },
    }
  );
  return { storeProposal: mutate, isProposalLoading: isLoading };
};
export const useGetDiseases = () => {
  const { userDetails } = useGetUserDetails();
  const alias = userDetails?.ic_detail?.ic_alias;
  const { data, isLoading } = useQuery(
    ["Diseases", alias],
    () => api.post(`/${alias === "care_health" ? "religare" : alias}/diseases`),
    { enabled: !!alias }
  );
  return { diseases: data?.data?.data, isDiseaseLoading: isLoading };
};
export const useRelationsQuery = () => {
  const { userDetails } = useGetUserDetails();
  const ic_id = userDetails?.ic_detail?.ic_id;
  const { data, isLoading } = useQuery(
    ["Relation", ic_id],
    () => api.post(`get/master/relationship`, { ic_id }),
    { enabled: !!ic_id }
  );
  return { relationsData: data?.data?.data, isRelationLoading: isLoading };
};
export const useSubmitProposal = () => {
  const { userDetails } = useGetUserDetails();
  const selected_plan = userDetails?.selected_plan;
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { proposerPrimaryId } = useSelector(proposalSelector);
  const { redirect } = useGetRedirection();
  const { mutate, isLoading } = useMutation(
    () =>
      api.post(`save-proposal`, {
        enquiry_id,
        primary_proposer_id: proposerPrimaryId,
        ic_id: selected_plan?.ic_id,
        travel_insurance_id: selected_plan?.travel_insurance_id,
      }),
    {
      onSuccess: async (data) => {
        if (data?.data[0]?.status) {
          toast.success(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              Proposal Submitted Successfully
            </div>
          );
          if (selected_plan?.ic_id === 1) {
            carePayment(
              data?.data?.[0]?.data?.proposalNum,
              userDetails?.enquiry_id
            );
          }
          if (selected_plan?.ic_id !== 1) {
            redirect({
              enquiry_id,
              payment_id: data.data[0].data?.payment_id,
            });
          }
        } else {
          toast.error(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              {data?.data[0]?.message || "Failed to Save Proposal"}
            </div>
          );
          if (selected_plan?.ic_id === 6) {
            Actions.setCKycFailCount(1);
            Actions.setMetaData(data?.data[0]);
          }
        }
        if (data?.data[0]?.data?.payment_url) {
          window.open(data?.data[0]?.data?.payment_url, "_self");
          return;
        }
      },
    }
  );
  return { submitProposal: mutate, isSubmitProposalLoading: isLoading };
};
export const useGetPolicyPDF = () => {
  const { userDetails } = useGetUserDetails();
  const selected_plan = userDetails?.selected_plan;
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { proposerPrimaryId } = useSelector(proposalSelector);
  const { data, mutate, isLoading } = useMutation(
    () =>
      api.post(`get-policy`, {
        enquiry_id,
        proposerPrimaryId,
        ic_id: selected_plan?.ic_id,
        travel_insurance_id: selected_plan?.travel_insurance_id,
      }),
    {
      enabled: !!enquiry_id || !!proposerPrimaryId || !!selected_plan,
      onSuccess: async (data) => {
        if (data?.data[0]?.status === true) {
          toast.success(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              Policy PDF Generated Successfully
            </div>
          );
        } else {
          toast.error(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              {data?.data[0]?.message || "Failed to Generate Policy PDF"}
            </div>
          );
        }
      },
      onError: () => {
        toast.error(
          <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
            Failed to Download policy PDF. Please try again later.
          </div>
        );
      },
    }
  );
  return {
    getPolicyPDF: mutate,
    policyPDFData: data,
    isPolicyPDFLoading: isLoading,
  };
};
export const useGetRedirection = () => {
  const navigate = useNavigate();
  const { userDetails } = useGetUserDetails();
  const selectedPlan = userDetails?.selected_plan;
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const savePaymentLogs = async (response, orderId, enquiryId) => {
    await fetch(`${process.env.REACT_APP_API_BASE_URL}save-logs`, {
      keepalive: true,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...response,
        order_id: orderId,
        payment_id: response.paymentId,
        razorpay_order_id: orderId,
        enquiry_id: enquiryId,
      }),
    });
  };
  const handleRazorpayRedirection = async (enquiry_id, data) => {
    const { data: order } = await api.post("create-order", { enquiry_id });
    const options = {
      key: process.env.REACT_APP_KEY_ID_RAZORPAY_TEST,
      amount: order.data.amount,
      currency: "INR",
      name: userDetails.ic_detail?.ic_name || "Fyntune Solution",
      description: "Travel Insurance",
      image: userDetails.ic_detail?.logo,
      order_id: order.data.id,
      handler: async (response) => {
        await savePaymentLogs(response, order.data.id, enquiry_id);
        await api.post("verify-payment", {
          ...response,
          order_id: order.data.id,
        });
      },
      modal: {
        ondismiss: () =>
          toast.error(
            "<div style='font-size:.8rem;font-weight:600;'>Payment Failed !!</div>"
          ),
      },
      prefill: {
        email: userDetails?.proposer_email,
        contact: userDetails?.proposer_mobile_no,
      },
      theme: { color: "#45b4d9" },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on("payment.failed", () => {});
    paymentObject.open();

    if (data.data[0].status) {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}travel_policy_pdf`, {
        keepalive: true,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enquiry_id,
          ic_id: selectedPlan?.ic_id,
          policy_id: data.data[0].data?.policy_id,
          payment_id: data.data[0].data?.payment_id,
        }),
      });
      navigate("/thank-you");
    }
  };
  const handleRedirection = async (data) => {
    const { redirection_url, redirection_method, redirection_params } =
      data?.data?.data[0];
    if (redirection_method === "POST") {
      const form = document.createElement("form");
      form.action = redirection_url;
      form.method = "POST";
      form.innerHTML = `<input name="pgiRequest" value='${redirection_params}' />`;
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } else if (redirection_method === "GET") {
      window.open(redirection_url, "_self");
    } else {
      await handleRazorpayRedirection(enquiry_id, data);
    }
  };
  const { mutate } = useMutation(
    (data) => api.post("quote/redirection", data),
    { onSuccess: handleRedirection }
  );
  return { redirect: mutate };
};
export const useCkycVerification = () => {
  const { userDetails } = useGetUserDetails();
  const leadData = useSelector(leadDataSelector);
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { mutate, isLoading } = useMutation(
    (req) => {
      Actions.setCKycUploadLoader({ [req?.docType]: true });
      return api.post("/ckyc-verifications", {
        ic_id: userDetails?.selected_plan.ic_id,
        company_alias: leadData?.ic_detail?.ic_alias,
        enquiry_id,
        section: "travel",
        type: "api",
        ...req,
      });
    },
    {
      onSuccess: (data, variables, context) => {
        Actions.setCKycUploadLoader({ [variables?.docType]: false });
        Actions.setCKycResponse(data?.data?.data?.data);
        if (data?.data?.data?.data?.verification_status == false || data?.data?.status == false) {
          Actions.setCKycFailCount(1);
          Actions.setCKycUploadStatus({ [variables?.docType]: true });
          toast.error(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              CKYC Verification Failed !!
            </div>
          );
        }
        if (
          data?.data?.data?.data?.verification_status == true ||
          data?.data?.verification_status === true
        ){
          Actions.setCKycFailCount(0);
          Actions.setCKycStatus(true);
        }
        
        if (data?.data?.status && _.isEmpty(data?.data?.data?.data)) {
          Actions.setCKycUploadStatus({ [variables?.docType]: true });
          toast.success(
            <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
              Document Uploaded Successfully !!
            </div>
          );
        }
      },
      onError: ({ error, variables, context }) => {
        Actions.setCKycUploadLoader({ [variables?.docType]: false });
        Actions.setCKycUploadStatus({ [variables?.docType]: false });
        toast.error(
          <div style={{ fontSize: ".8rem", fontWeight: "600" }}>
            Document Uploaded Failed !!
          </div>
        );
      },
    }
  );
  return { verifyCkyc: mutate, isCkycLoading: isLoading };
};
export const useGetTermsAndConditions = () => {
  const { userDetails } = useGetUserDetails();
  const icId = userDetails?.ic_detail?.ic_id;
  const { data, isLoading } = useQuery(
    ["Terms and Conditions", icId],
    () => api.get(`companies/${icId}/terms_and_conditions`),
    { enabled: !!icId }
  );
  return {
    termsAndCondition: data?.data?.company_terms_and_conditions,
    isTermsAndConditionLoading: isLoading,
  };
};
